import React, { SyntheticEvent, useEffect, useState } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title } from "chart.js";
import MapImage from "../components/Map";
import MyTabs from "../components/Tabs";
import ExportButton from "../components/Export";
import MapSelector from "../components/MapSelector"; // Import the new component
//import "./Explorer.css";
import { AttributeResponse, MetadataRespone, ResolutionKeys, StateToShow, UserDefinedDataset, WaterTye } from '../types';
import YearSelectSlider from '../components/Slider';
import ColumnSelector from '../components/ColumnSelector';
import TimeSeriesChart from '../components/TimeSeries';
import Histogram from '../components/Histogram';
import { handle_page_load, handle_dataset_change, handle_attribute_change, handle_resolution_or_year_or_month_change, handle_session_load, get_dataset_list } from "../functions/backend_handlers";
import DynamicSelector from '../components/DynamicSelector';
import ExportTemplate from '../components/ExportTemplate';
import ImportButton from '../components/Import';
import useSessionStorage from "../components/Session";
import { water_type_titles, water_type_values } from '../constants';
import WaterTypeRadio from '../components/WaterTypeRadio';
import { ActionMeta, SingleValue } from 'react-select';
import MonthSelectSlider from '../components/SliderMonth';
import { hashObject } from '../functions/miscellaneous';

// Register the required components with Chart.js
ChartJS.register(CategoryScale, LinearScale, BarElement, Title);

interface DatasetOption {
    value: string;
    label: string;
    category: string;
  }

const Explorer: React.FC = () => {

    //user_defined_datasets
    const [available_user_datasets, setAvailableUserDatasets] = useState<UserDefinedDataset>({}); // ("explorer_available_user_datasets",{})
    const [current_user_dataset, setCurrentUserDataset] = useSessionStorage<string>("explorer_current_user_dataset","");
    const [selectedOverlay, setSelectedOverlay] = useSessionStorage<string>("explorer_selectedOverlay","None");

    // colorings
    const [colormap_types, setColormapTypes] = useState<string>("");
    const [colormap_bases, setColormapBases] = useState<string>("");
    const [attribute_legend_labels, setAttributeLegendLabels] = useState<string>("");

    //secondary states
    const [available_resolutions, setAvailableResolutions] = useState<ResolutionKeys[]>([]);
    const [polygon_values, setPolygonValues] = useState<{ [key: string]: string; }>({});
    const [histogram_data, setHistogramData] = useState<{ "label": string[], "value": number[] }>({ label: [], value: [] });
    const [timeseries_data, setTimeseriesData] = useState<{x:string[], y:number[]}>({x:[],y:[]});
    const [has_time, setHasTime] = useState<boolean>(false);
    const [valid_years, setValidYears] = useState<number[]>([]);
    const [valid_months, setValidMonths] = useState<number[]>([]);
    const [show_coloring, setShowColoring] = useState<boolean>(true);

    const [is_overlay, setIsOverlay] = useState<string>("False");
    const [geojson_frame, setGeojsonFrame] = useState<GeoJSON.FeatureCollection | undefined>(undefined);
    const [popup_name, setPopupName] = useState<{ [key: string]: string }>({});


    const [is_legend, setIsLegend] = useState<string>("ON");

    //const [attributes_list, setAttributesList] = useState<{ names: string[]; titles: string[] }>({ names: [], titles: [] });

    const [available_datasets, setAvailableDatasets] = useState<{ categories: string[], names: string[]; titles: string[] }>({categories:[], names: [], titles: [] });
    const [attribute_min, setAttributeMin] = useState<number | undefined>(undefined);
    const [attribute_max, setAttributeMax] = useState<number | undefined>(undefined);
    const [attribute_categories, setAttributeCategories] = useState<{ [key: string]: number }>({});
    const [polygon_type, setPolygonType] = useState<"Continuous" | "Discrete">("Continuous");
    const [available_water_types, setAvailableWaterTypes] = useState<WaterTye[]>([undefined]);
    const [current_dataset_level, setCurrentDatasetLevel] = useState< "1" | "2" | "3" | null>(null);


    const [is_log, setIsLog] = useState<boolean>(false);
    const [log_polygon_values, setLogPolygonValues] = useState<{[key: string]: string}>({});
    const [popup_description, setPopupDescriptions] = useState<{[key: string]: string[]} | undefined>();
    const [log_min, setLogMin] = useState<number | undefined>(undefined);
    const [log_max, setLogMax] = useState<number | undefined>(undefined);
    const [log_histogram_data, setLogHistogramData] = useState<{"label" : string[], "value": number[]}>({ label: [], value: [] });

    //main states
    const [current_resolution, setCurrentResolution] = useSessionStorage<ResolutionKeys>("explorer_current_resolution", "state");  // useState setCurrentResolution
    const [current_dataset, setCurrentDataset] = useSessionStorage<string>("explorer_current_dataset", ""); //
    const [current_attribute, setCurrentAttribute] = useSessionStorage<string>("explorer_current_attribute", ""); //
    const [current_year, setCurrentYear] = useSessionStorage<number | undefined>("explorer_current_year", 0); //
    const [current_month, setCurrentMonth] = useSessionStorage<number | undefined>("explorer_current_month", 0); //
    const [current_parameters, setCurrentParameters] = useSessionStorage<StateToShow>("explorer_current_parameters", {}) //
    const [current_water_type, setCurrentWaterType] = useState<WaterTye>(undefined);


    const [previous_resolution, setPreviousResolution] = useSessionStorage<ResolutionKeys>("explorer_previous_resolution", "state"); 

    const [data_is_missing, setDataIsMissing] = useState(false);

    const [attributes_list, setAttributesList] = useSessionStorage<{ names: string[]; titles: string[] }>("explorer_attributes_list",{ names: [], titles: [] });

    const [zoomLevel, setZoomLevel] = useSessionStorage<number>("explorer_zoomLevel", 3.2)
    const [center, setCenter] = useSessionStorage<[number, number]>('explorer_center', [38.5, -99.3]);

    const setShowUserDataset = (user_data: UserDefinedDataset, current_user_data: string) => {
        setPolygonValues(user_data[current_user_data].polygon_values) // polygon_values
        setAttributeMin(Number(user_data[current_user_data].min)) // min
        setAttributeMax(Number(user_data[current_user_data].max))  // max
        setCurrentResolution(user_data[current_user_data].resolution) // resolution
        setAttributeCategories(user_data[current_user_data].categories) // categories
        setPolygonType(user_data[current_user_data].polygon_type) // polygon_type

        //set color type
        setColormapTypes(user_data[current_user_data].colormap_types)

        //set color bases
        setColormapBases(user_data[current_user_data].colormap_bases)

        // Extra stuff
        setHasTime(false);
        setHistogramData({ label: [], value: [] });
    };

    const updateMetadata = (metadata: MetadataRespone) => {
        if (metadata.Data.is_overlay == "True") {
            setIsLog(false);
            setAvailableResolutions([]);
        }
        else setAvailableResolutions(metadata.Data.available_resolutions);

        setAttributesList({ names: metadata.Data.available_attributes, titles: metadata.Data.available_attribute_titles });
        setValidYears(metadata.Data.available_years.map(value => Number(value)));
        setHasTime(metadata.Data.has_time);
        setCurrentDataset(metadata.Data.dataset_name);
        setCurrentAttribute(metadata.Data.attribute_name);
        setCurrentYear(Number(metadata.Data.available_years[0]));
        setColormapTypes(metadata.Data.colormap_types);
        setAttributeLegendLabels(metadata.Data.attribute_legend_labels);
        setAvailableWaterTypes(metadata.Data.available_water_types);
        setCurrentDatasetLevel(metadata.Data.dataset_level);
        setValidMonths(metadata.Data.available_months.map(value => Number(value)));
        setCurrentMonth(Number(metadata.Data.available_months[0]));
        setIsOverlay(metadata.Data.is_overlay);
        setGeojsonFrame(metadata.Data.geojson_frame);
        setPopupName(metadata.Data.popup_name);
    };

    const updateAttribute = (attribute: AttributeResponse) => {
        setPolygonValues(attribute.Data.polygon_values);
        setHistogramData(attribute.Data.histogram_data);
        setCurrentResolution(attribute.Data.resolution);
        setAttributeMin(Number(attribute.Data.min));
        setAttributeMax(Number(attribute.Data.max));
        setAttributeCategories(attribute.Data.categories);
        setPolygonType(attribute.Data.polygon_type);
        setCurrentWaterType(attribute.Data.water_type);
        setLogPolygonValues(attribute.Data.log_polygon_values)
        setLogMin(Number(attribute.Data.log_min))
        setLogMax(Number(attribute.Data.log_max))
        setLogHistogramData(attribute.Data.log_histogram_data)
        setTimeseriesData(attribute.Data.timeseries_data);
        setPopupDescriptions(attribute.Data.popup_description);
    };

    const setEmptyData = () => {
        setCurrentAttribute("");
        setCurrentDataset("");
        setPolygonValues({})
        setCurrentUserDataset("");
    }

    useEffect(() => {
        if (Object.keys(polygon_values).length > 0){
            setShowColoring(true);
            setIsLegend("ON");
        } else {
            setShowColoring(false);
            setIsLegend("OFF");
        }
    }, [polygon_values]);

    useEffect(() => {
        if (Object.keys(polygon_values).length === 0) setDataIsMissing(true);
        else setDataIsMissing(false);
    }, [polygon_values]);

    const setEmptyMap = () => {
        setPolygonValues({});
        setTimeseriesData({x:[],y:[]});
        setHistogramData({ label: [], value: [] });
    }

    const handleDataSetChangeOnDropdown = (option: DatasetOption | null) => {
        if(option){
            if(option.category == "Custom Datasets"){ // user defined
                onUserDatasetChange({value:option.value , label:option.label}, undefined)
            } else {
                handleDatasetChange(option.value)
                setShowColoring(true)
                setIsLegend("ON")
                setCurrentUserDataset("");
            }

        } else {
            setEmptyData();
        }
    };

    const handleAttributeChangeOnDropdown = (option: any) => {
        handleAttributeChange(option.value)
    };

    const handleDatasetChange = async (dataset: string) => {
        setCurrentDataset(dataset);
        const { metadata, attribute, current_parameters } = await handle_dataset_change(dataset, current_resolution, current_water_type);
        setCurrentParameters(current_parameters);
        updateMetadata(metadata);

        if(metadata.Data.is_overlay == "True"){
            if(current_resolution != "overlay")
                setPreviousResolution(current_resolution);
        } else {
                if(!metadata.Data.available_resolutions.includes(previous_resolution)){
                    setCurrentResolution(previous_resolution);
                } else {
                    setCurrentResolution(metadata.Data.available_resolutions[0]);
                }

        }
        
        if(attribute) updateAttribute(attribute);
        else setEmptyMap();
    };

    const handleAttributeChange = async (attribute: string) => {
        setCurrentAttribute(attribute);
        const { metadata, attribute: attrData, current_parameters } = await handle_attribute_change(current_dataset, attribute, current_resolution, current_water_type);
        setCurrentParameters(current_parameters);
        updateMetadata(metadata);
        if(attrData) updateAttribute(attrData);
        else setEmptyMap();
    };

    const handleResolutionOrYearOrMonthChange = async ({ resolution, year, month }: { resolution?: ResolutionKeys; year?: number; month?:number }) => {
        if (resolution) setCurrentResolution(resolution);
        else resolution = current_resolution;
        
        if (year) setCurrentYear(Number(year));
        else year = current_year;
        
        if (month) setCurrentMonth(Number(month))
        else month = current_month;

        const attribute = await handle_resolution_or_year_or_month_change(current_dataset, current_attribute, current_parameters, resolution, year, month, current_water_type, is_overlay);

        setCurrentParameters(current_parameters);
        if(attribute) updateAttribute(attribute);
        else setEmptyMap();
    };

    useEffect(() => {
        const storedDatasetsString = localStorage.getItem("user_datasets");
        const  storedDatasets = storedDatasetsString ? JSON.parse(storedDatasetsString) : {}
        setAvailableUserDatasets(storedDatasets);

        

        if (current_dataset === "" && current_user_dataset === "") {
            handle_page_load(true).then(({ metadata, attribute, current_parameters, datalist }) => {

                // Append the keys from storedDatasets to datalist
                Object.keys(storedDatasets).forEach(key => {
                    datalist.names.push(key);
                    datalist.titles.push(key); // Using the same key as the title
                    datalist.categories.push("Custom Datasets"); // Fixed category for all
                });
                setCurrentParameters(current_parameters);
                setAvailableDatasets(datalist);
                updateMetadata(metadata);
                if(attribute) updateAttribute(attribute);
                else setEmptyMap();
            }).catch((error: any) => {
                console.log("No data with these parameters!")
                setEmptyData();
            });
        }
        else {
            // available_datasets
            get_dataset_list().then(({datalist}) => {

                // Append the keys from storedDatasets to datalist
                Object.keys(storedDatasets).forEach(key => {
                    datalist.names.push(key);
                    datalist.titles.push(key); // Using the same key as the title
                    datalist.categories.push("Custom Datasets"); // Fixed category for all
                });
                setAvailableDatasets(datalist)
            })

            if(current_user_dataset === ""){
                handle_session_load(current_dataset, current_attribute, current_parameters, current_resolution, current_year, current_month, current_water_type)
                    .then(({ metadata, datalist, attribute: attrData }) => {
                            setCurrentParameters(current_parameters);
                            setCurrentAttribute(current_attribute);
                            
                            // Append the keys from storedDatasets to datalist
                            Object.keys(storedDatasets).forEach(key => {
                                datalist.names.push(key);
                                datalist.titles.push(key); // Using the same key as the title
                                datalist.categories.push("Custom Datasets"); // Fixed category for all
                            });
                            setAvailableDatasets(datalist);

                            updateMetadata(metadata);
                            if(attrData) updateAttribute(attrData);
                            else setEmptyMap();
                    }).catch((error: any) => {
                        console.log("No data with these parameters!")
                        setEmptyData();
                    });
            } else {
                onUserDatasetChange({value:current_user_dataset , label:current_user_dataset}, undefined)
            }
        }

    }, []);


    const handleTabChangeOnSelect = (event: SyntheticEvent, newValue: ResolutionKeys) => {

        if(!show_coloring){
            if (newValue) setCurrentResolution(newValue);
            return;
        }

        handleResolutionOrYearOrMonthChange({ resolution: newValue });
    };

    // Handle the slider change
    const handleYearSliderChange = (event: Event, newValue: number | number[]) => {
        // newValue is the index of the selected year
        handleResolutionOrYearOrMonthChange({ year: valid_years[newValue as number] });
    };

    const handleMonthSliderChange = (event: Event, newValue: number | number[]) => {
        // newValue is the index of the selected year
        handleResolutionOrYearOrMonthChange({ month: valid_months[newValue as number] });
    };

    const handleDynamicSelectorChange = async (key: string, value: string) => {
        // Directly modify the current_parameters object
        const updatedParameters = { ...current_parameters };

        if (updatedParameters[key]) {
            updatedParameters[key].current_code = value;
        }

        const attribute = await handle_resolution_or_year_or_month_change(current_dataset, current_attribute, updatedParameters, current_resolution, current_year, current_month, current_water_type, is_overlay);
        setCurrentParameters(updatedParameters);
        if(attribute) updateAttribute(attribute);
        else setEmptyMap();
    };

    const handleWaterTypeChange = async (value: string) => {
        const updated_water_type = value as WaterTye;
        const attribute = await handle_resolution_or_year_or_month_change(current_dataset, current_attribute, current_parameters, current_resolution, current_year, current_month, updated_water_type, is_overlay);
        if(attribute) updateAttribute(attribute);
        else setEmptyMap();
    };

    const onUserDatasetChange = (selectedOption : SingleValue<{value: string; label: string; }>, actionMeta: ActionMeta<{value: string;label: string;}> | undefined ) => {
        if(selectedOption){
            setCurrentUserDataset(selectedOption.value);
            setShowUserDataset(available_user_datasets, selectedOption.value);
            setAvailableResolutions([]);
            setShowColoring(true);
            setCurrentAttribute("");
            setCurrentDataset("");
            setIsLegend("ON")
        } else {
            setEmptyData()
        }
    }

    const handleLogCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newChecked = e.target.checked;
        setIsLog(newChecked);
      };

    useEffect(() => {
    console.log("Explorer mounted with zoom:", zoomLevel, "center:", center);
    }, []);

    return (
        <>
            <div className="contact-page-container">
                <div className="contact-page-left">
                    <MyTabs key={`MyTabs_${current_resolution}_${available_resolutions.toString()}`} selectedTab={current_resolution} onTabChange={handleTabChangeOnSelect} tabStatus={available_resolutions} />
                    <div className="contact-page-map-container">
                        
                        { is_log && polygon_type=="Continuous" &&
                        <MapImage resolution={current_resolution} 
                            polygon_values={log_polygon_values}
                            min={log_min} 
                            max={log_max} 
                            categories={attribute_categories}
                            polygon_type={polygon_type} attribute_name={current_attribute}
                            current_dataset={current_dataset}
                            colormap_bases={colormap_bases}
                            colormap_types={colormap_types}
                            attributes_list={attributes_list}
                            available_datasets={available_datasets}
                            selectedOverlay={selectedOverlay}
                            attribute_legend_labels={ current_user_dataset != "" ? current_user_dataset : attribute_legend_labels}
                            is_legend={is_legend}
                            show_coloring={show_coloring}
                            is_hml={false}
                            is_log={is_log}
                            is_overlay={is_overlay}
                            geojson_frame={geojson_frame}
                            popup_name={popup_name}
                            popup_description={popup_description}
                            key={`map-${hashObject(polygon_values)}-${is_legend}`} 
                            current_parameters={current_parameters} 
                            
                            zoomLevel = {zoomLevel}
                            setZoomLevel = {setZoomLevel}
                            center = {center}
                            setCenter = {setCenter}
                        />
                        }      
                        { (!is_log || polygon_type=="Discrete") &&
                        <MapImage resolution={current_resolution} polygon_values={polygon_values}
                        min={attribute_min} max={attribute_max} categories={attribute_categories}
                        polygon_type={polygon_type} attribute_name={current_attribute}
                        current_dataset={current_dataset}
                        colormap_bases={colormap_bases}
                        colormap_types={colormap_types}
                        attributes_list={attributes_list}
                        available_datasets={available_datasets}
                        selectedOverlay={selectedOverlay}
                        attribute_legend_labels={current_user_dataset != "" ? current_user_dataset : attribute_legend_labels}
                        is_legend={is_legend}
                        show_coloring={show_coloring}
                        is_hml={false}
                        is_log={is_log}
                        is_overlay={is_overlay}
                        geojson_frame={geojson_frame}
                        popup_name={popup_name}
                        popup_description={popup_description}
                        key={`map-${hashObject(polygon_values)}-${is_legend}`} 
                        current_parameters={current_parameters}     
                        
                        zoomLevel = {zoomLevel}
                        setZoomLevel = {setZoomLevel}
                        center = {center}
                        setCenter = {setCenter}
                        />
                        }   
                    </div>
                </div>

                <div className="contact-page-right">
                    <div className="scrollable-content">


                        <h3 className="custom-dataset-title">Dataset Selection</h3>
                        {( available_datasets.names.length > 0 && (
                            <MapSelector
                                key={`MapSelector_${current_dataset}`}
                                data={available_datasets}
                                onSelect={handleDataSetChangeOnDropdown}
                                baseValue={current_dataset ? current_dataset : (current_user_dataset ? current_user_dataset : null)}
                                is_clearable={true}
                                available_resolutions={available_resolutions}
                            />
                        ))}
                        { current_dataset !== "" &&  <div>
                        {
                            <ColumnSelector 
                            key={`ColumnSelector_${current_attribute}`}
                            options={attributes_list.names.map((name, index) => ({ 
                                value: name, 
                                label: attributes_list.titles[index] || name
                            }))} 
                            onSelect={handleAttributeChangeOnDropdown} 
                            baseValue={(current_attribute ? {
                                label: attributes_list.titles[attributes_list.names.indexOf(current_attribute)], 
                                value: current_attribute
                            } : null)} 
                            />
                        }
                        {current_dataset_level == "3" && current_water_type && <WaterTypeRadio available_values={available_water_types} key={`WaterTypeRadio_${current_water_type}`} title={"Water Type"} type={"radio"} values={water_type_values} labels={water_type_titles} name={current_water_type} selectedValue={current_water_type} onChange={(value) => value ? handleWaterTypeChange(value) : null} />}

                        {Object.keys(current_parameters).map((key) => {

                            const { type, titles, codes, current_code } = current_parameters[key as keyof typeof current_parameters];
                            const title = key.split("_")[0];

                            return (
                                <DynamicSelector
                                    key={`DynamicSelector_${key}`}
                                    title={title}  // For now, just join titles as a simple label
                                    type={type}
                                    values={codes}
                                    labels={titles}
                                    name={key}
                                    selectedValue={current_code}
                                    onChange={(value) => handleDynamicSelectorChange(key, value)}
                                />
                            );
                        })}

                        {(has_time && (current_year !== undefined && !isNaN(current_year)) && <YearSelectSlider key={`YearSelectSlider_${current_year}`} value={current_year} handleYearSliderChange={handleYearSliderChange} years={valid_years} />)}

                        {(has_time && (current_month !== undefined && !isNaN(current_month)) && <MonthSelectSlider key={`MonthSelectSlider_${current_month}`} value={current_month} handleMonthSliderChange={handleMonthSliderChange} months={valid_months} />)}
                        
                        {data_is_missing && (
                            <div style={{ 
                                backgroundColor: '#FFF3E0',
                                border: '1px solid #FFB74D',
                                borderRadius: '4px',
                                padding: '6px 12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '13px',
                                color: '#E65100',
                                marginTop: '25px',
                            }}>
                                <span style={{ fontSize: '14px' }}>⚠️</span>
                                <span>There are no data available for these inputs!</span>
                            </div>
                        )}
                        
                        
                        { polygon_type == "Continuous" && is_overlay == "False" &&
                        <div className="filter-section">
                            <div className="log-scale-wrapper">
                            <input
                                type="checkbox"
                                id="log-scale"
                                checked={is_log}
                                onChange={handleLogCheckboxChange}
                                className="log-scale-checkbox"
                            />
                            <label htmlFor="log-scale" className="log-scale-label">
                                Log Scale
                            </label>
                            </div>
                        </div>
                        }
                        
                        
                        {(histogram_data.label.length > 0 || (has_time && (timeseries_data.x.length > 0)))  && <span className="filter-section-title">{"Data Visualization"}</span>}
                        {(!is_log || polygon_type=="Discrete") && (histogram_data.label.length > 0) && <Histogram data={histogram_data} is_log={is_log}/>}
                        {is_log && polygon_type == "Continuous" && (histogram_data.label.length > 0) && <Histogram data={log_histogram_data} is_log={is_log}/>}
                        {(has_time && (timeseries_data.x.length > 0) && <TimeSeriesChart data={timeseries_data} />)}


                        <div className="export-container">
                            <ExportButton 
                                polygon_values={polygon_values} 
                                resolution={current_resolution} 
                                dataset={current_dataset} 
                                attribute={current_attribute} 
                                parameters={current_parameters} 
                                year={current_year} 
                            />
                            </div>
                        </div> }

                    </div>

                    <div className="explorer-fixed-footer">
                        <div style={{padding:"50px 10px"}}>
                            <h3 className="custom-dataset-title">Upload Your Own Data</h3>
                            <div className="button-group">
                                <ExportTemplate resolution={current_resolution} is_overlay={is_overlay} />
                                <ImportButton 
                                    setAvailableDatasets={setAvailableDatasets} 
                                    current_datalist={available_datasets} 
                                    setAvailableResolutions={setAvailableResolutions} 
                                    setAvailableUserDatasets={setAvailableUserDatasets} 
                                    setCurrentUserDataset={setCurrentUserDataset} 
                                    setShowUserDataset={setShowUserDataset} 
                                    is_overlay={is_overlay}
                                />
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </>
    );
};

export default Explorer;